import {useMemo} from 'react'
import {useCheckEnrollment, useAvailablePoints, useCheckTier} from './useAnnexCloud'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getLoyaltyEnrollment} from 'hec/helpers/getLoyaltyEnrollment'
import {perksAndRewardsSectionStateMap} from 'hec/builder/blocks/account/perks-and-rewards-section/utils'

interface UsePerksAndRewardsOptions {
    isEdit?: boolean
    stateToShowInEditor?: string
}

/**
 * Extracts all perks & rewards data-fetching so it can be called at the page
 * level to kick off requests early, and consumed by PerksAndRewardsSection
 * via the shared react-query cache.
 */
const usePerksAndRewards = ({
    isEdit = false,
    stateToShowInEditor
}: UsePerksAndRewardsOptions = {}) => {
    const {site} = useMultiSite()
    const {
        data: customerData,
        isError: isCustomerError,
        error: customerError
    } = useCurrentCustomer()

    const customer = customerData

    const {hasData: hasAcLoyaltyData} = useMemo(
        () => getLoyaltyEnrollment(customer?.c_acLoyaltyData, site.id),
        [customer?.c_acLoyaltyData, site.id]
    )

    const {data: enrollment} = useCheckEnrollment(customer?.email, site.id, {
        enabled: hasAcLoyaltyData
    })
    const isEnrolled = enrollment?.data?.optInStatus === 'YES'

    const {data: availablePoints} = useAvailablePoints(customer?.email, site.id, {
        enabled: isEnrolled
    })
    const {data: tierData} = useCheckTier(customer?.email, site.id, {enabled: isEnrolled})

    const pointsEarned: number = availablePoints?.data?.availablePoints || 0
    const currentTier = useMemo(() => tierData?.data?.currentTier as string | undefined, [tierData])

    const userState = useMemo(() => {
        if (isEdit) return stateToShowInEditor
        if (!customer) return undefined

        const isLoggedIn = !customer.isGuest && customer.isRegistered
        if (!isLoggedIn) return perksAndRewardsSectionStateMap.LOGGED_OUT
        if (!isEnrolled) return perksAndRewardsSectionStateMap.NOT_ENROLLED
        if (pointsEarned <= 0) return perksAndRewardsSectionStateMap.ENROLLED_WITH_NO_POINTS
        return perksAndRewardsSectionStateMap.ENROLLED_WITH_POINTS
    }, [customer, isEnrolled, pointsEarned, isEdit, stateToShowInEditor])

    return {
        customer,
        site,
        isCustomerError,
        customerError,
        isEnrolled,
        pointsEarned,
        currentTier,
        userState
    }
}

export default usePerksAndRewards
